import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { SidebarComponent } from '@shared/components/sidebar/sidebar';
import { DotFieldComponent } from '@shared/components/dot-field/dot-field';
import { SidebarService } from '@shared/services/sidebar.service';
import { TitleService } from '@shared/services/title.service';
import { NavSectionsService } from '@shared/services/nav-sections.service';
import { HeaderActionsService } from '@shared/services/header-actions.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBell,
  lucideCircleHelp,
  lucideMessageSquarePlus,
  lucideEye,
  lucidePause,
  lucideZap,
  lucideMapPin,
  lucidePencil,
  lucideArrowLeft,
  lucidePlay,
  lucidePlus,
  lucideRefreshCw,
  lucideExternalLink,
} from '@ng-icons/lucide';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmLeadDirective } from '@spartan-ng/helm/typography';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { NotificationStore } from '@shared/stores/notification.store';
import { PageTabsService } from '@shared/services/page-tabs.service';
import { PageSaveBarService } from '@shared/services/page-save-bar.service';
import { NotificationSheetComponent } from '@shared/components/notification-sheet/notification-sheet';
import { PageHeaderMetaService } from '@shared/services/page-header-meta.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { BackNavigationService } from '@shared/services/back-navigation.service';
import { SupportDialog } from '@features/help/components/support-dialog/support-dialog';

export function isItemDetailRoute(route: ActivatedRouteSnapshot): boolean {
  while (route.firstChild) route = route.firstChild;
  const segment = route.routeConfig?.path?.split('/').at(-1);
  return (
    (segment?.startsWith(':') && segment.toLowerCase().endsWith('id')) ?? false
  );
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    DotFieldComponent,
    NgIconComponent,
    ...HlmCardImports,
    HlmButtonDirective,
    HlmLeadDirective,
    NotificationSheetComponent,
    SupportDialog,
  ],
  providers: [
    provideIcons({
      lucideBell,
      lucideCircleHelp,
      lucideMessageSquarePlus,
      lucideEye,
      lucidePause,
      lucideZap,
      lucideMapPin,
      // Used by pages that expose an "edit" quick action (e.g. settings).
      lucidePencil,
      lucideArrowLeft,
      lucidePlay,
      lucidePlus,
      lucideRefreshCw,
      lucideExternalLink,
    }),
  ],
  templateUrl: './layout.html',
  styleUrl: './layout.css',
  host: {
    class: 'block',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Layout {
  protected readonly currentOrganization = inject(CurrentOrganizationStore);
  protected sidebarService = inject(SidebarService);
  protected titleService = inject(TitleService);
  protected notificationStore = inject(NotificationStore);
  protected headerActions = inject(HeaderActionsService);
  protected pageTabs = inject(PageTabsService);
  protected pageHeaderMeta = inject(PageHeaderMetaService);
  protected saveBar = inject(PageSaveBarService);
  private navSections = inject(NavSectionsService);
  private router = inject(Router);
  private readonly backNavigation = inject(BackNavigationService);
  private navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ),
    { initialValue: null },
  );

  /** Section / page — always the sidebar's own section title + item label, so the
   *  header breadcrumb can never drift from what the sidebar actually shows. */
  breadcrumb = this.navSections.activeBreadcrumb;
  headerSubtitle = computed(
    () => this.pageHeaderMeta.meta()?.subtitle ?? this.titleService.pageSubtitle(),
  );
  headerTitle = computed(
    () => this.pageHeaderMeta.meta()?.title ?? this.titleService.pageTitle(),
  );
  headerBreadcrumbLabel = computed(
    () => this.pageHeaderMeta.meta()?.breadcrumbLabel ?? this.headerTitle(),
  );
  headerDescription = computed(() => this.pageHeaderMeta.meta()?.description ?? null);
  fullBleed = computed(() => this.pageHeaderMeta.meta()?.fullBleed === true);
  headerTitleActions = computed(() => this.pageHeaderMeta.meta()?.titleActions ?? []);
  headerBadges = computed(() => this.pageHeaderMeta.meta()?.badges ?? []);
  compactHeaderBadges = computed(
    () => this.pageHeaderMeta.meta()?.compactBadges ?? false,
  );
  headerBackAction = computed(() => {
    this.navigationEnd();
    return isItemDetailRoute(this.router.routerState.snapshot.root)
      ? { ariaLabel: 'Zurück', onClick: () => this.backNavigation.back() }
      : null;
  });
  headerActionsTemplate = computed(
    () => this.pageHeaderMeta.meta()?.actionsTemplate ?? null,
  );
}
